import { formatCommasNumber } from "@/common/util/format-number-with-commas";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { UserDto } from "@/types/dto/user-dto";
import { Table } from "flowbite-react";
import { useState } from "react";
import { BusinessType } from "../../../common/enum/business-type";
import { PartnerBusinessCertificateModal } from "../../modal/PartnerBusinessCertificateModal";
import { H4 } from "../../typography/H4";
import { CustomCell } from "../CustomCell";

interface Props {
  user: UserDto;
}

export const PartnerInfoTable: React.FC<Props> = ({ user }) => {
  const [openBizCertModal, setOpenBizCertModal] = useState(false);
  const headCellStyle = "font-medium text-gray-900 bg-gray-100";

  const handleOpenBizCertModal = () => {
    setOpenBizCertModal(true);
  };

  const handleCloseBizCertModal = () => {
    setOpenBizCertModal(false);
  };

  return (
    <div className="overflow-x-auto flex flex-col gap-[16px]">
      {openBizCertModal && (
        <PartnerBusinessCertificateModal
          imageUrl={user.businessCertificateUrl}
          closeModal={handleCloseBizCertModal}
        />
      )}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <Table.Body>
            <Table.Row className="border-b border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    파트너명
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.name}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    상호명
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] h-[52px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.businessName || ""}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
            <Table.Row className="border-b border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    파트너 등급
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.grade.name}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    파트너 구분
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.businessType === BusinessType.COMPANY
                      ? "법인 사업자"
                      : "개인 사업자"}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
            <Table.Row className="border-b border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    이메일(아이디)
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.email}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    연락처
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.phone}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
            <Table.Row className="border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    주소
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {`[${user.postalCode}] ${user.road} ${user.detailAddress}`}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    사업자번호
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[680px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.businessNumber}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <Table.Body>
            <Table.Row className="bg-grayscale-base">
              <Table.Cell className="p-0  h-[90px] w-[538.67px]">
                <div className="flex flex-col gap-[8px] p-[16px] ">
                  <div>
                    <Body14Regular className="text-semantic-text-tertiary">
                      구매 건 수
                    </Body14Regular>
                  </div>
                  <div>
                    <H4 className="text-semantic-text-body">
                      {user.orderCount}
                    </H4>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className="p-0 border-l  h-[90px] w-[538.67px]">
                <div className="flex flex-col gap-[8px] p-[16px] ">
                  <div>
                    <Body14Regular className="text-semantic-text-tertiary">
                      총 실결제 금액
                    </Body14Regular>
                  </div>
                  <div>
                    <H4 className="text-semantic-text-body">
                      {formatCommasNumber(user.totalSales)}
                    </H4>
                  </div>
                </div>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};
